import clsx from 'clsx';
import { type ButtonHTMLAttributes, type ForwardedRef, forwardRef } from 'react';
import type { Color } from './colors';
import type { Size } from './layout';
export const BUTTON_PURPOSES = [
  'primary',
  'outline',
  'discreet',
  'default',
  'flat',
  'link',
] as const;
export type ButtonPurpose = (typeof BUTTON_PURPOSES)[number];

export const BUTTON_LOADING_SPINNER_POSITIONS = ['start', 'end'] as const;
export type ButtonLoadingSpinnerPosition = (typeof BUTTON_LOADING_SPINNER_POSITIONS)[number];

export const BUTTON_ICON_POSITIONS = ['start', 'end', 'icon-only'] as const;
export type ButtonIconPosition = (typeof BUTTON_ICON_POSITIONS)[number];

export const BUTTON_STATES = ['default', 'active', 'disabled'] as const;
export type ButtonState = (typeof BUTTON_STATES)[number];

type NoColorPurpose = {
  purpose?: Extract<ButtonPurpose, 'link' | 'default'>;
  color?: never;
};
type ColorPurpose = {
  purpose?: Exclude<ButtonPurpose, 'link' | 'default'>;
  color?: Color;
};
export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  block?: boolean;
  iconPosition?: ButtonIconPosition;
  loading?: boolean;
  loadingSpinnerPosition?: ButtonLoadingSpinnerPosition;
  size?: Size;
  state?: ButtonState;
} & (NoColorPurpose | ColorPurpose);

/**
 * Button with the attributes found in Sigma design.
 *
 * @example
 * <Button purpose="primary" color="primary" size="lg" iconPosition="start">
 *   <Icon icon="check" />
 *   Action
 * </Button>
 */
export const Button = forwardRef((props: ButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
  const {
    block: isBlock,
    children,
    className,
    iconPosition,
    loading: isLoading = false,
    loadingSpinnerPosition = 'start',
    onClick,
    size = 'md',
    state = 'default',
    purpose = 'default',
    color,
    ...otherProps
  } = props;

  const componentClassName = clsx(
    ['btn', getButtonStyleClass(purpose, color), `btn-${size}`, className],
    {
      'btn-icon-start': iconPosition === 'start',
      'btn-icon-end': iconPosition === 'end',
      'btn-icon': iconPosition === 'icon-only',

      'btn-block': isBlock,

      active: state === 'active',
      disabled: state === 'disabled',
    },
  );

  return (
    <button {...otherProps} ref={ref} onClick={onClick} className={componentClassName}>
      {loadingSpinnerPosition === 'start' && isLoading && (
        <span className="spinner blinker-sm me-2" role="status" />
      )}
      {children}
      {loadingSpinnerPosition === 'end' && isLoading && (
        <span className="spinner blinker-sm ms-2" role="status" />
      )}
    </button>
  );
});

const getButtonStyleClass = (purpose: ButtonProps['purpose'], color: Color | undefined) => {
  switch (purpose) {
    case 'default':
      return 'btn-default';

    case 'link':
      return 'btn-link';

    case 'primary':
      return `btn-${color ?? 'primary'}`;

    case 'outline':
      return `btn-outline-${color ?? 'primary'}`;

    case 'discreet':
      return `btn-discreet-${color ?? 'primary'}`;

    case 'flat':
      return `btn-flat-${color ?? 'primary'}`;
  }
};
